import * as React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useGetNotificationSettings } from 'src/features/parkingManagement/api/NotificationsAPIs/useGetNotificationSettings';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import NotificationModal from 'src/features/parkingManagement/components/Notifications/NotificationModal';
import { NOTIFICATIONS, type Notification } from 'src/features/parkingManagement/types/notifications';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { NotificationsTableColumns } from 'src/features/parkingManagement/utils/notificationsTableColumns';
import { GeneralBlockSubtitle, GeneralBlockTitle } from 'src/registry/components/blocks/GeneralBlock';
import { DataTable } from 'src/registry/components/table';
import { Separator } from 'src/registry/ui/Separator';
import { useTitleStore } from 'src/stores/useTitleStore';

const Notifications = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { data } = useGetParking({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });
    const { setTitle, removeTitle } = useTitleStore();
    const [selectedNotification, setSelectedNotification] = React.useState<Notification | undefined>(undefined);
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        return () => removeTitle();
    }, [data?.features, formatMessage]);

    const { data: notificationSettings, refetch } = useGetNotificationSettings({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });

    const getDefaultNotifications = (notificationType: NOTIFICATIONS) => {
        return {
            id: '',
            notification_type: notificationType,
            is_enabled: false,
            recipient_roles: [],
        };
    };

    const getModalActionType = (notificationType: NOTIFICATIONS): 'edit' | 'create' => {
        const notificationAlreadyCreated = notificationSettings?.results.find(
            (notification) => notification.notification_type === notificationType,
        );

        return notificationAlreadyCreated ? 'edit' : 'create';
    };

    const notificationReservationCancelledByClient = notificationSettings?.results.find(
        (notification) =>
            notification.notification_type === NOTIFICATIONS.RESERVATION_CANCELLED_BY_CLIENT_NOTIFY_OPERATOR,
    );
    const notificationReservationCreatedByClient = notificationSettings?.results.find(
        (notification) =>
            notification.notification_type === NOTIFICATIONS.RESERVATION_CREATED_BY_CLIENT_NOTIFY_OPERATOR,
    );

    const dataTable = React.useMemo(() => {
        return {
            count: 2,
            results: [
                ...[
                    notificationReservationCancelledByClient
                        ? notificationReservationCancelledByClient
                        : getDefaultNotifications(NOTIFICATIONS.RESERVATION_CANCELLED_BY_CLIENT_NOTIFY_OPERATOR),
                ],
                ...[
                    notificationReservationCreatedByClient
                        ? notificationReservationCreatedByClient
                        : getDefaultNotifications(NOTIFICATIONS.RESERVATION_CREATED_BY_CLIENT_NOTIFY_OPERATOR),
                ],
            ] as Notification[],
        };
    }, [notificationReservationCancelledByClient, notificationReservationCreatedByClient]);

    const handleNotificationModal = (notification?: Notification, isOpen: boolean = false) => {
        setSelectedNotification(notification);
        setIsModalOpen(isOpen);
    };

    const openNotificationModal = (notification: Notification) => handleNotificationModal(notification, true);

    const columns = NotificationsTableColumns({
        openNotificationModal,
    });

    return (
        <>
            <div className="flex flex-col gap-2">
                <GeneralBlockTitle>
                    <FormattedMessage {...parkingManagementMessages.notifications} />
                </GeneralBlockTitle>
                <GeneralBlockSubtitle>
                    <FormattedMessage {...parkingManagementMessages.notificationsSubtitle} />
                </GeneralBlockSubtitle>
            </div>
            <Separator />
            <DataTable
                columns={columns}
                data={dataTable}
                semantics={{
                    title: formatMessage(parkingManagementMessages.notifications),
                    entryName: formatMessage(parkingManagementMessages.notification),
                    entryNames: formatMessage(parkingManagementMessages.notifications),
                }}
                enableLocalFilters={true}
            />
            {isModalOpen && selectedNotification && (
                <NotificationModal
                    actionType={getModalActionType(selectedNotification.notification_type)}
                    notification={selectedNotification}
                    setIsModalOpen={setIsModalOpen}
                    refetch={refetch}
                />
            )}
        </>
    );
};

export default Notifications;
